import React from "react";
import { usePaymentTheme } from "../respayment/hooks/usePaymentTheme";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import Image from "src/shared/Image";
import { OrderedProduct, Product } from "src/shared/services/interfacev2";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";
import BarcodeScanner from "src/shared/components/barcode/BarcodeScanner";
import { notifyError } from "src/assets/toastService";

interface ProductGridProps {
  products: Product[];
  selectedProducts: OrderedProduct[];
  handleIncrement: (item: Product) => void;
  handleDecrement: (item: Product) => void;
}

const ProductGrid: React.FC<ProductGridProps> = ({ products, selectedProducts, handleIncrement, handleDecrement }) => {
  const truncateText = (text: string, length: number) => (text.length > length ? `${text.slice(0, length)}...` : text);

  const { getLightBgClass, themeTextColor, themeBorderColor, themeRingColor, fontClass } = usePaymentTheme();

  const getQuantity = (item: Product) => {
    const selected = selectedProducts.find((order) => order.productId === item._id.$oid);
    return selected?.quantity ?? 0;
  };

  const lightBgClass = getLightBgClass();
  const textColorClass = themeTextColor;
  const borderColorClass = themeBorderColor;
  const formatAmount = useFormatAmount();

  const handleSearchProductByBarcode = (barcodeId: string) => {
    const barcodeFilter = products.filter((i) => i.barcodeId === barcodeId)[0];
    if (barcodeFilter) {
      handleIncrement(barcodeFilter);
    } else {
      notifyError("Product product available")
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 w-full overflow-hidden p-2 bg-gray-50/30 isolate">
      <div className="flex-1 h-full overflow-y-auto thin-scrollbar custom-scrollbar grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-5 auto-rows-max gap-3 p-1.5 w-full align-top pr-2">
        {products.map((item) => {
          const quantity = getQuantity(item);
          const hasQuantity = quantity > 0;

          return (
            <div key={item._id.$oid} onClick={() => handleIncrement(item)}
              className={`group relative z-0 hover:z-10 flex flex-col justify-between bg-white rounded-xl shadow-sm border border-gray-200 transition-shadow duration-200 cursor-pointer overflow-hidden hover:shadow-md ${
                hasQuantity ? `ring-2 ${themeRingColor} border-${borderColorClass.split("-")[1]}-300` : ""
              } h-full min-h-[210px]`}
            >
              <div className="relative w-full h-[110px] shrink-0 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden rounded-t-xl">
                <Image imagePath={item.imageUrl} className="w-full h-full object-cover" />

                {hasQuantity && (
                  <div className={`absolute top-2 right-2 bg-white ${textColorClass} rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg border-2 ${themeBorderColor}`}>
                    {quantity}
                  </div>
                )}
              </div>

              <div className="flex flex-col flex-1 p-2.5 justify-between">
                <div>
                  <h3 className={`text-sm font-semibold text-gray-900 leading-tight mb-0.5 truncate ${fontClass}`}>{truncateText(item.productName, 20)}</h3>

                  {item.arabicName && <h4 className="text-xs text-gray-500 font-normal truncate leading-relaxed">{truncateText(item.arabicName, 35)}</h4>}
                </div>

                <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-100">
                  <span className={`text-base font-bold ${themeTextColor}`}>{formatAmount(item.amount)}</span>

                  <div className="flex items-center gap-1">
                    {hasQuantity ? (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleDecrement(item); }}
                          className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-bold hover:bg-gray-200 transition-colors"> −</button>

                        <span className="text-sm font-semibold text-gray-700 min-w-[20px] text-center">{quantity}</span>

                        <button onClick={(e) => { e.stopPropagation(); handleIncrement(item); }}
                          className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-bold hover:bg-gray-200 transition-colors"> + </button>
                      </>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); handleIncrement(item); }}
                        className={`w-7 h-7 rounded-lg ${lightBgClass} ${textColorClass} flex items-center justify-center text-sm font-semibold hover:scale-110 transition-transform opacity-90 hover:opacity-100`}>
                        <RestaurantMenuIcon fontSize="small" className={textColorClass} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Active State Indicator */}
              <div
                className={`absolute inset-0 border-2 border-transparent rounded-xl group-hover:border-${borderColorClass.split("-")[1]}-200 transition-all duration-300 pointer-events-none ${hasQuantity ? `border-${borderColorClass.split("-")[1]}-300` : ""
                  }`}
              />
            </div>
          );
        })}
      </div>

      <div className="hidden">
        <BarcodeScanner onDetected={(code: string) => handleSearchProductByBarcode(code)} />
      </div>

      {products.length === 0 && (
        <div className="flex flex-col items-center justify-center h-full text-gray-500 py-10">
          <p className="text-lg font-medium mb-1">No products found</p>
          <p className="text-sm text-gray-400">Try selecting a different category</p>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
